import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject, OnDestroy } from '@angular/core';
import { ModalAddTrailService } from './modal-add-trail.service';
import { Store } from '@ngrx/store';
import { AppState } from '@app/store/app.state';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LearningTrailsService } from '@app/core';
import { BehaviorSubject, filter, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { mapTypeToIcon } from '@app/modules/learning-trails/utils/type-mapper';
import { ModalFileViewerComponent } from '@app/modules/eclass/components/modal-file-viewer/modal-file-viewer.component';
import { AppSelectors } from '@app/store';
import { safeEmpty, safeEmptyList } from '@app/shared/utils';
import { TypeFile } from '@app/core/models';
import { SHARED_MODULES } from '@app/shared';
import { CardSubdivisionComponent } from './card-subdivision/card-subdivision.component';
import { CardTrailComponent } from './card-trail/card-trail.component';
import { LearningTrailsModule } from '@app/modules/learning-trails/learning-trails.module';

@Component({
  imports: [SHARED_MODULES, CardSubdivisionComponent, CardTrailComponent, LearningTrailsModule],
  templateUrl: './modal-add-trail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalAddTrailComponent implements OnDestroy {
  public readonly data = inject(DIALOG_DATA);
  public readonly modalAddTrailService = inject(ModalAddTrailService);
  private readonly _dialog = inject(Dialog);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _store = inject(Store<AppState>);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _learningTrailsService = inject(LearningTrailsService);
  public form: FormGroup;
  public collections$: Observable<any>;
  public loadingCollections$ = new BehaviorSubject<boolean>(false);
  public courses$ = new BehaviorSubject<any[]>(undefined);
  public loadingCourses$ = new BehaviorSubject<boolean>(false);
  public typeIcon = mapTypeToIcon;

  private _destroy$ = new Subject<void>();

  constructor() {
    this.initForm();
    this.loadCollections();
    this.modalAddTrailService.elementSelected$ = undefined;
    this.modalAddTrailService.forceSelectedElements(this.data.selectedElements);
  }

  public ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this.modalAddTrailService.elementSelected$ = undefined;
  }

  public onCloseModal() {
    this._dialogRef.close();
  }

  public onSave(elements: any) {
    this._dialogRef.close({
      elements,
    });
  }

  public onOpenAnnex(annex: any) {
    this._dialog.open<ModalFileViewerComponent>(ModalFileViewerComponent, {
      data: this.formatFile(annex),
    });
  }

  private initForm() {
    this.form = this._formBuilder.group({
      collection: [undefined, Validators.required],
      courseFilter: undefined,
    });

    this.form.controls['collection'].valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.modalAddTrailService.elementSelected$ = undefined;
      this.courses$.next(undefined);
      this.form.controls['courseFilter'].setValue(undefined);
      this.loadCourses();
    });
  }

  private loadCollections() {
    this.collections$ = this._store.select(AppSelectors.ActiveUser).pipe(
      takeUntil(this._destroy$),
      tap(() => this.loadingCollections$.next(true)),
      filter((user) => !!user),
      switchMap(({ entidade_id, entidade_tipo_id }) =>
        this._learningTrailsService.showcaseCollectionsEclass({ entidade_id, entidade_tipo: 1 }),
      ),
      safeEmptyList(),
      tap(() => this.loadingCollections$.next(false)),
    );
  }

  private loadCourses(page = 1) {
    if (this.form.invalid) {
      return;
    }

    this._store
      .select(AppSelectors.ActiveUser)
      .pipe(
        takeUntil(this._destroy$),
        tap(() => this.loadingCourses$.next(true)),
        filter((user) => !!user),
        switchMap(({ entidade_id }) =>
          this._learningTrailsService.showcaseEclass({
            entidade_id,
            entidade_tipo: 1,
            colecao_id: this.form.value.collection,
          }),
        ),
        safeEmpty(),
        tap(() => this.loadingCourses$.next(false)),
      )
      .subscribe((courses) => {
        this.courses$.next(courses);
      });
  }

  private formatFile(file: any): File {
    const formattedFile: any = {};
    formattedFile.title = file.nome_arquivo;
    formattedFile.mimetype = file.mimetype;

    switch (file.extensao) {
      case 'xlsx':
      case 'pptx':
      case 'docx':
        formattedFile.type = TypeFile.Office;
        formattedFile.file = file.file;
        break;
      case 'png':
      case 'jpg':
      case 'jpeg':
        formattedFile.type = TypeFile.Image;
        formattedFile.file = file.file;
        break;
      case 'pdf':
        formattedFile.type = TypeFile.Pdf;
        formattedFile.file = file.file;
        break;
      case 'mp4':
      case 'mov':
      case 'm4v':
        formattedFile.type = TypeFile.Video;
        formattedFile.file = file.video;
        formattedFile.vimeo_hash = file.vimeo_hash;
        break;
      case 'mp3':
        formattedFile.type = TypeFile.Audio;
        formattedFile.file = file.file;
        break;
    }

    return formattedFile;
  }
}
